import * as Sentry from "@sentry/react";
import { Suspense } from "react";
import { HelmetProvider } from "react-helmet-async";
import { Route, Routes } from "react-router-dom";

import CustomerLayout from "./components/layout/CustomerLayout";
import Layout from "./components/layout/Layout";
import { ErrorScreen, LoadingScreen } from "./components/layout/Loading";
import { Toaster } from "./components/ui/sonner";

import { PrivateRoute } from "./PrivateRoute";
import { TokenContractRoute } from "./TokenContractRoute";

import { AuthProvider } from "./contexts/auth-context";
import { LocationProvider } from "./contexts/location-context";
import { PendenciasProvider } from "./contexts/pendencias-context";
import { QuoteProvider } from "./contexts/quote-context";

import ForgotPassword from "./pages/auth/forgot-password";
import CreateNewPassword from "./pages/auth/forgot-password/create-new-password";
import VerifyRecoveryCode from "./pages/auth/forgot-password/verify-code";
import Login from "./pages/auth/login";

import ContractsPage from "./pages/private/contracts";
import DetailsContractPage from "./pages/private/detailsContract";
import AbrirChamadoPage from "./pages/private/faqSupport/abrirChamado";
import ChamadosAbertosPage from "./pages/private/faqSupport/chamadosAbertos";
import HistoricoDeChamadosPage from "./pages/private/faqSupport/historicoDeChamados";
import PerguntasFrequentesPage from "./pages/private/faqSupport/perguntasFrequentes";
import TicketStatusPage from "./pages/private/faqSupport/ticketStatus";
import HomePage from "./pages/private/home";
import OpenQuotePage from "./pages/private/openQuote";
import PersonalDataPage from "./pages/private/personalData";
import ResumePage from "./pages/private/resumo";

import LayoutEcommerce from "./components/layout/layout-ecommerce";
import CookiesPage from "./pages/public/cookies";
import QuoteDetails from "./pages/public/cotacao/[quoteId]";
import FaqPublic from "./pages/public/faq";
import Quote from "./pages/public/fazer-cotacao";
import Home from "./pages/public/home";
import About from "./pages/public/nacional/about";
import BlogHomePage from "./pages/public/nacional/blog";
import BlogPostPage from "./pages/public/nacional/blog-post";
import CidadesPage from "./pages/public/nacional/cidades";
import EquipePage from "./pages/public/nacional/equipe";
import ParaSuaEmpresaPage from "./pages/public/nacional/para-sua-empresa";
import PraVocePage from "./pages/public/nacional/pra-voce";
import ServicosPage from "./pages/public/nacional/servicos";
import TestimonialPage from "./pages/public/nacional/testimonial";
import TipoDeVeiculoPage from "./pages/public/nacional/tipo-de-veiculo";
import TransportePage from "./pages/public/nacional/transporte";
import NotFound from "./pages/public/notFound";
import PrivacidadePage from "./pages/public/privacidade";
import TermosDeUso from "./pages/public/terms";

import CityPage from "./pages/public/city";
import { regions } from "./utils/locales";

function App() {
  return (
    <HelmetProvider>
      <AuthProvider>
        <PendenciasProvider>
          <LocationProvider>
            <QuoteProvider>
              <Toaster />
              <Sentry.ErrorBoundary fallback={<ErrorScreen />}>
                <Suspense fallback={<LoadingScreen />}>
                  <Routes>
                    <Route path="/" element={<Layout />}>
                      <Route index element={<Home />} />
                      <Route path="login" element={<Login />} />

                      <Route
                        path="esqueci-senha"
                        element={<ForgotPassword />}
                      />
                      <Route
                        path="esqueci-senha/verify-code/:email"
                        element={<VerifyRecoveryCode />}
                      />
                      <Route
                        path="esqueci-senha/criar-nova-senha/:email/:code"
                        element={<CreateNewPassword />}
                      />
                      <Route path="sobre" element={<About />} />
                      <Route path="equipe" element={<EquipePage />} />
                      <Route
                        path="tipo-de-veiculo"
                        element={<TipoDeVeiculoPage />}
                      />
                      <Route path="pra-voce" element={<PraVocePage />} />
                      <Route path="servicos" element={<ServicosPage />} />
                      <Route
                        path="para-sua-empresa"
                        element={<ParaSuaEmpresaPage />}
                      />
                      <Route path="blog" element={<BlogHomePage />} />
                      <Route path="blog/:slug" element={<BlogPostPage />} />
                      <Route path="faq" element={<FaqPublic />} />
                      <Route path="depoimentos" element={<TestimonialPage />} />
                      <Route path="cidades" element={<CidadesPage />} />
                      <Route
                        path="transporte-de-veiculos"
                        element={<TransportePage />}
                      />
                      {regions.flatMap((region) =>
                        region.cities.map((c) => (
                          <Route
                            key={c.id}
                            path={`transporte-de-veiculos-${c.slug}`}
                            element={<CityPage slug={c.slug} />}
                          />
                        )),
                      )}
                    </Route>

                    <Route element={<LayoutEcommerce />}>
                      <Route
                        path="/cotacao/:quoteId"
                        element={<QuoteDetails />}
                      />
                      <Route path="/termos" element={<TermosDeUso />} />
                      <Route
                        path="/privacidade"
                        element={<PrivacidadePage />}
                      />
                      <Route path="/cookies" element={<CookiesPage />} />
                      <Route path="fazer-cotacao" element={<Quote />} />
                    </Route>

                    <Route
                      path="/cliente"
                      element={
                        <PrivateRoute>
                          <CustomerLayout />
                        </PrivateRoute>
                      }
                    >
                      <Route index element={<HomePage />} />
                      <Route path="resumo" element={<ResumePage />} />

                      <Route path="contratos" element={<ContractsPage />} />
                      <Route
                        path="dados-pessoais"
                        element={<PersonalDataPage />}
                      />
                      <Route
                        path="cotacoes-abertas"
                        element={<OpenQuotePage />}
                      />
                      <Route
                        path="contratos-abertos"
                        element={<ContractsPage />}
                      />

                      <Route
                        path="status-ticket"
                        element={<TicketStatusPage />}
                      />

                      <Route
                        path="ajuda/abrir-chamado"
                        element={<AbrirChamadoPage />}
                      />
                      <Route
                        path="ajuda/chamados-abertos"
                        element={<ChamadosAbertosPage />}
                      />
                      <Route
                        path="ajuda/historico-de-chamados"
                        element={<HistoricoDeChamadosPage />}
                      />
                      <Route
                        path="ajuda/perguntas-frequentes"
                        element={<PerguntasFrequentesPage />}
                      />
                    </Route>
                    <Route
                      path="/cliente/detalhes-contrato/:id"
                      element={
                        <TokenContractRoute>
                          <CustomerLayout />
                        </TokenContractRoute>
                      }
                    >
                      <Route index element={<DetailsContractPage />} />
                    </Route>

                    <Route path="*" element={<NotFound />} />
                  </Routes>
                </Suspense>
              </Sentry.ErrorBoundary>
            </QuoteProvider>
          </LocationProvider>
        </PendenciasProvider>
      </AuthProvider>
    </HelmetProvider>
  );
}

export default App;
